import type { TouchEventHandler } from 'react'
import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useSelector, useDispatch } from 'react-redux'

import { Paragraph } from 'src/styles/typography/typography'
import { sendTrackingRequestAction } from 'client/contexts/Tracking/actions'
import { clickZoomCarouselDesktop } from 'client/contexts/Tracking/events'
import { useProperty } from 'src/hooks/useProperty'
import { useDevice } from 'src/hooks/useDevice'
import Modal from 'client/components/Modal'
import PhotosGallery from 'src/comps/Molecules/PhotosGallery'
import { getPropertyPricing, getIsPrimeBestPrice } from 'src/effects/Availability/selectors'

import MainImage from './MainImage'
import Images from './Images'
import {
  GalleryBase,
  ShowMore,
  StyledDiscountTag,
  StyledCarouselDots,
  GalleryTitle,
  GalleryContainer,
} from './styles'
import { ZoomCTA } from '../Molecules/ZoomCTA'
import { BreakpointSizes } from 'src/types/device'
import { useKafkaEvent } from 'src/hooks/useKafkaEvent'
import { getSearchPaxes } from 'src/effects/Search/selectors'
import { getLocation } from 'client/contexts/Location/selectors'
import { calculateDiscount } from 'client/utils'
import { MAX_ALLOWED_THUMBNAILS } from './constants'
import { ShowMoreThumbnail } from './ShowMoreThumbnail'

interface Props {
  onPriceClick: () => void
  onVisibilityChange?: (isVisible: boolean) => void
}

const Gallery = ({ onPriceClick, onVisibilityChange }: Props) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const { sendEvent: sendKafkaClickHotelGallery } = useKafkaEvent({ page: 'hotel_details' })

  const occupancies = useSelector(getSearchPaxes)
  const locationState = useSelector(getLocation)

  const { activeBreakpoint } = useDevice()
  const property = useProperty()
  const { photos, name, stars } = property

  const { initialBestPrice, totalDiscount } = useSelector(getPropertyPricing)

  const isPrime = useSelector(getIsPrimeBestPrice)

  const isMobile = activeBreakpoint < BreakpointSizes.M
  const isTablet = activeBreakpoint >= BreakpointSizes.M && activeBreakpoint < BreakpointSizes.L

  const [currentSlide, setCurrentSlide] = useState(0)
  const [showCarousel, setShowCarousel] = useState(false)
  const [slideX, setSlideX] = useState(0)
  const [timer, setTimer] = useState<number>()

  useEffect(() => {
    onVisibilityChange?.(showCarousel)
  }, [showCarousel, onVisibilityChange])

  useEffect(() => {
    return () => {
      if (!timer) return
      clearTimeout(timer)
    }
  }, [timer])

  function handleTracking() {
    dispatch(sendTrackingRequestAction({ event: clickZoomCarouselDesktop() }))
    sendKafkaClickHotelGallery(
      { section: 'hotel_images' },
      {
        occupancies,
        search: {
          country: locationState?.country,
          type: locationState?.citySlug ? 'city' : 'zone',
          slug: locationState?.slug,
        },
      }
    )
  }

  const handleClick = (index = 0) => {
    setCurrentSlide(index)
    setShowCarousel(true)
    handleTracking()
  }

  const closeModal = () => {
    setShowCarousel(false)
  }

  const closeModalAndScroll = () => {
    closeModal()
    const timerClick = window.setTimeout(() => {
      onPriceClick()
    }, 0)
    setTimer(timerClick)
  }

  const initSlide = currentSlide === 0
  const lastSlide = currentSlide === photos.length - 1

  const startSlide: TouchEventHandler<HTMLDivElement> = event => {
    setSlideX(event.changedTouches[0].clientX)
  }

  function prevSlide() {
    return !initSlide && setCurrentSlide(currentSlide - 1)
  }

  function nextSlide() {
    return !lastSlide && setCurrentSlide(currentSlide + 1)
  }

  const changeSlide: TouchEventHandler<HTMLDivElement> = event => {
    const slideDiff = event.changedTouches[0].clientX - slideX
    const direction = Math.sign(slideDiff)

    if (direction < 0) nextSlide()
    if (direction > 0) prevSlide()

    setSlideX(0)
  }

  const pointerEvents = {
    onTouchEnd: changeSlide,
    onTouchStart: startSlide,
  }

  const discount = calculateDiscount(initialBestPrice, totalDiscount)

  const showMoreThumbnail = photos[MAX_ALLOWED_THUMBNAILS + 1]
  return (
    <GalleryContainer>
      {discount && <StyledDiscountTag discount={discount} prime={isPrime} />}
      <GalleryBase>
        <MainImage handleClick={handleClick} pointerEvents={pointerEvents} photos={photos} />
        <Images
          isMobile={isMobile}
          isTablet={isTablet}
          handleClick={handleClick}
          pointerEvents={pointerEvents}
          photos={photos.slice(1)}
        />
        {showMoreThumbnail ? (
          <ShowMoreThumbnail
            handleClick={handleClick}
            image={showMoreThumbnail}
            photosCount={photos.length - 1}
          />
        ) : (
          <ShowMore onClick={() => handleClick()}>
            <Paragraph color="grey8" size="m">
              {intl.formatMessage({ id: 'detail.show-all-photos' })}
            </Paragraph>
          </ShowMore>
        )}
      </GalleryBase>
      <StyledCarouselDots dots={photos} current={currentSlide} />
      <Modal
        title={<GalleryTitle>{intl.formatMessage({ id: 'global.hotel-images' })}</GalleryTitle>}
        show={showCarousel}
        variant="fullscreen"
        handleClose={closeModal}
        displayHeader
        displayHeaderShadow
      >
        <PhotosGallery
          focusOnImageIndex={currentSlide}
          photos={photos}
          CTA={<ZoomCTA name={name} stars={stars} onClick={closeModalAndScroll} />}
        />
      </Modal>
    </GalleryContainer>
  )
}

export default Gallery
